import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Flag, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const REPORT_TYPES = [
  { value: 'incorrect_answer', label: 'Incorrect Answer' },
  { value: 'incorrect_question', label: 'Incorrect Question' },
  { value: 'outdated_information', label: 'Outdated Information' },
  { value: 'duplicate_question', label: 'Duplicate Question' },
  { value: 'other', label: 'Other' }
];

const ReportQuestionModal = ({ open, onClose, questionId, questionText, questionType = 'oral' }) => {
  const [reportType, setReportType] = useState('');
  const [description, setDescription] = useState('');
  const [suggestedCorrection, setSuggestedCorrection] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!reportType) {
      toast.error('Please select a report type');
      return;
    }
    if (!description.trim()) {
      toast.error('Please provide a description');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const endpoint = questionType === 'written' 
        ? `${API}/reports/written-question` 
        : `${API}/reports/question`;
      
      await axios.post(endpoint, {
        question_id: questionId,
        report_type: reportType,
        description: description.trim(),
        suggested_correction: suggestedCorrection.trim() || null
      }, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      
      toast.success('Report submitted successfully. Thank you for your feedback!');
      handleClose();
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Please login to report a question');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to submit report');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setReportType('');
    setDescription('');
    setSuggestedCorrection('');
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600">
            <Flag className="w-5 h-5" />
            Report Question
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Question Preview */}
          <div className="bg-gray-50 p-3 rounded-lg border">
            <p className="text-sm text-gray-600 mb-1">Question:</p>
            <p className="text-sm font-medium text-gray-800 line-clamp-3">
              {questionText || 'Question text not available'}
            </p>
          </div>

          {/* Report Type */}
          <div className="space-y-2">
            <Label htmlFor="report-type">Report Type *</Label>
            <Select value={reportType} onValueChange={setReportType}>
              <SelectTrigger>
                <SelectValue placeholder="Select what's wrong" />
              </SelectTrigger>
              <SelectContent>
                {REPORT_TYPES.map(type => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">Description *</Label>
            <Textarea
              id="description"
              placeholder="Please describe what's wrong with this question or answer..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
            />
          </div>

          {/* Suggested Correction */}
          <div className="space-y-2">
            <Label htmlFor="suggestion">
              Suggested Correction <span className="text-gray-400 text-xs">(optional)</span>
            </Label>
            <Textarea
              id="suggestion"
              placeholder="If you know the correct information, please share it here..."
              value={suggestedCorrection}
              onChange={(e) => setSuggestedCorrection(e.target.value)}
              rows={2}
            />
          </div>

          {/* Info Notice */}
          <div className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 p-3 rounded-lg border border-amber-200">
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <p>
              Your report will be reviewed by our team. We appreciate your help in 
              maintaining the accuracy of our question bank.
            </p>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={handleClose} disabled={submitting}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={submitting}
            className="bg-red-600 hover:bg-red-700"
          >
            {submitting ? 'Submitting...' : 'Submit Report'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ReportQuestionModal;
